import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Phone, Search, Hash } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { T } from "@/context/TextEditContext";

function fmtPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const PHONE_KEY = "bk_loyalty_phone";

export default function TrackPage() {
  const [mode, setMode] = useState("phone"); // "phone" | "order"
  const [phone, setPhone] = useState(
    () => localStorage.getItem(PHONE_KEY) || ""
  );
  const [orderNumber, setOrderNumber] = useState("");
  const [checking, setChecking] = useState(false);
  const [multiHits, setMultiHits] = useState([]);
  const navigate = useNavigate();

  // Auto-lookup on first mount if we have a saved phone
  useEffect(() => {
    if (phone && phone.replace(/\D/g, "").length >= 7) {
      handlePhoneLookup(phone, /* silent */ true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhoneLookup = async (raw, silent = false) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.length < 7) {
      if (!silent) toast.error("Enter a valid phone number");
      return;
    }
    setChecking(true);
    try {
      const { data } = await api.get(`/orders/by-phone/${digits}`);
      if (!data || data.length === 0) {
        if (!silent) toast.error("No active orders found for that number");
        setMultiHits([]);
        return;
      }
      localStorage.setItem(PHONE_KEY, digits);
      data.forEach((o) => {
        if (o.track_token && o.order_number) {
          localStorage.setItem(`bk_track_${o.order_number}`, o.track_token);
        }
      });
      if (data.length === 1) {
        navigate(`/order/${data[0].order_number}`);
      } else {
        setMultiHits(data);
      }
    } catch {
      if (!silent) toast.error("Lookup failed. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const handleOrderNumberLookup = async (e) => {
    e.preventDefault();
    const on = orderNumber.trim().toUpperCase();
    if (!on) return;
    setChecking(true);
    try {
      const token = localStorage.getItem(`bk_track_${on}`);
      await api.get(`/orders/track/${on}`, { params: token ? { t: token } : {} });
      navigate(`/order/${on}`);
    } catch (err) {
      if (err?.response?.status === 403) {
        // Order exists but this device hasn't proven ownership yet — the
        // order page will ask for the checkout phone number.
        navigate(`/order/${on}`);
      } else {
        toast.error("Order not found. Double-check the number.");
      }
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-100 flex justify-center">
      <div className="w-full max-w-md lg:max-w-2xl min-h-screen bg-[#FDFBF7] phone-shell rustic-paper">
        <div className="chalkboard-band px-4 py-3 flex items-center gap-2 border-b-4 border-brand-red">
          <Link
            to="/"
            className="p-1 rounded-full hover:bg-white/10"
            data-testid="back-home-link"
          >
            <ChevronLeft className="w-5 h-5 text-brand-yellow" />
          </Link>
          <div className="font-display text-brand-yellow text-xl">
            <T k="track.title" d="Track Order" />
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Mode toggle */}
          <div
            className="grid grid-cols-2 gap-2 bg-stone-200/70 rounded-full p-1"
            data-testid="track-mode-toggle"
          >
            <button
              type="button"
              onClick={() => setMode("phone")}
              data-testid="track-mode-phone"
              className={`py-2 rounded-full text-xs uppercase tracking-widest font-black flex items-center justify-center gap-1.5 transition-all ${
                mode === "phone"
                  ? "bg-brand-red text-white shadow"
                  : "text-stone-600"
              }`}
            >
              <Phone className="w-3 h-3" /> <T k="track.mode_phone" d="Phone" />
            </button>
            <button
              type="button"
              onClick={() => setMode("order")}
              data-testid="track-mode-order"
              className={`py-2 rounded-full text-xs uppercase tracking-widest font-black flex items-center justify-center gap-1.5 transition-all ${
                mode === "order"
                  ? "bg-brand-red text-white shadow"
                  : "text-stone-600"
              }`}
            >
              <Hash className="w-3 h-3" /> <T k="track.mode_order" d="Order #" />
            </button>
          </div>

          {mode === "phone" ? (
            <>
              <div className="text-center">
                <div className="font-hand text-3xl text-stone-500">
                  <T k="track.phone_headline" d="Skip the order number" />
                </div>
                <p className="text-sm text-stone-500 mt-1">
                  <T k="track.phone_sub" d="Just enter the phone you checked out with" />
                </p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handlePhoneLookup(phone);
                }}
                className="space-y-3"
              >
                <input
                  type="tel"
                  inputMode="tel"
                  value={fmtPhone(phone)}
                  onChange={(e) =>
                    setPhone(e.target.value.replace(/\D/g, ""))
                  }
                  placeholder="(479) 555-1234"
                  data-testid="track-phone-input"
                  className="w-full text-center px-4 py-3 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-lg font-mono tracking-wide"
                />
                <button
                  type="submit"
                  disabled={
                    checking || phone.replace(/\D/g, "").length < 7
                  }
                  data-testid="track-phone-submit"
                  className="w-full bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {checking ? "Looking..." : <T k="track.phone_btn" d="Find My Order" />}
                </button>
              </form>

              {multiHits.length > 1 && (
                <div
                  className="space-y-2"
                  data-testid="track-multi-hits"
                >
                  <div className="text-xs uppercase tracking-widest text-stone-500 font-black">
                    You have {multiHits.length} active orders — tap one
                  </div>
                  {multiHits.map((o) => (
                    <button
                      key={o.order_number}
                      onClick={() => navigate(`/order/${o.order_number}`)}
                      data-testid={`track-multi-hit-${o.order_number}`}
                      className="w-full text-left bg-white border-2 border-stone-200 hover:border-brand-red rounded-xl p-3 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="font-display text-brand-red text-lg">
                          #{o.order_number}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest font-black text-stone-500">
                          {o.status.replace(/_/g, " ")}
                        </div>
                      </div>
                      <div className="text-xs text-stone-500 mt-0.5">
                        {(o.items_count ?? o.items?.length) || 0} item
                        {(o.items_count ?? o.items?.length) === 1 ? "" : "s"} · ${o.total?.toFixed(2)}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-center">
                <div className="font-hand text-3xl text-stone-500">
                  Already ordered?
                </div>
                <p className="text-sm text-stone-500 mt-1">
                  Enter your order number to see the status
                </p>
              </div>

              <form onSubmit={handleOrderNumberLookup} className="space-y-3">
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="BK-12345"
                  data-testid="order-status-input"
                  className="w-full text-center px-4 py-3 rounded-lg border-2 border-stone-300 bg-white outline-none focus:border-brand-red text-lg font-display tracking-widest uppercase"
                />
                <button
                  type="submit"
                  disabled={checking || !orderNumber.trim()}
                  data-testid="order-status-check-btn"
                  className="w-full bg-brand-red text-white py-3 rounded-full font-black uppercase tracking-widest text-sm hover:bg-red-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                >
                  <Search className="w-4 h-4" />
                  {checking ? "Checking..." : "Check Status"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
